import { InfoOutlined } from "@mui/icons-material";
import {
  Accordion,
  AccordionDetails,
  AccordionSummary,
  Chip,
  Divider,
  Stack,
  Tooltip,
  Typography,
} from "@mui/material";
import { GridExpandMoreIcon } from "@mui/x-data-grid";
import dayjs from "dayjs";
import { useLocation } from "react-router-dom";
import { useMedicalCareContext } from "../../../../contexts/MedicalCareContext";
import { useQuestionnaireContext } from "../../../../contexts/QuestionnaireContext";
import { useAttendanceSpecialtyIndex } from "@/hooks/useAttendanceSpecialtyIndex";
import { buildQrDisplayInfo } from "@/utils/resolveAttendanceSpecialty";
import { StatusAndNote } from "../StatusAndNote";
import { CondutasHistory } from "./CondutasHistory";
import { scrollbarStyle } from "../scrollbarStyle";
import { AddConduta } from "./AddConduta";
import LuxorMed from "../../../../components/luxor/LuxorMed";

export function Condutas() {
  const { openDialog, condutas, setCondutas, luxorMode } = useMedicalCareContext();
  const { questionnaire, response, loadingResponse } = useQuestionnaireContext();
  const specialtyIndex = useAttendanceSpecialtyIndex();

  const { state } = useLocation();
  // const dataConducts = defaultValues?.[2]?.item;

  const condutasItems = questionnaire?.item?.find(
    (el: any) => el.text === "Condutas"
  );

  const responseEntries = response?.entry?.map((el: any) => el?.resource);

  const condutasReponses = responseEntries?.map((el: any) => {
    const awnserDate = el?.authored;
    const filterItens = el?.item
      ?.filter((e: any) => e?.text === "Condutas")?.[0]
      ?.item?.filter((subItem: any) => {
        return subItem?.item?.[0]?.answer;
      });
    const displayInfo = buildQrDisplayInfo(el, specialtyIndex);
    return { awnserDate, filterItens, displayInfo };
  });

  const flatOpenItens = condutasReponses
    ?.filter((el: any) => el?.filterItens?.length)
    ?.flatMap((item: any) => {
      // map para adicionar informações de profissional, healthcare e data ao item de cada resposta
      const auxItem = item?.filterItens?.map((response: any) => {
        return {
          ...response,
          displayInfo: item?.displayInfo,
          awnserDate: item?.awnserDate,
        };
      });

      return auxItem;
    });

  // Abordagem simplificada: apenas itens atuais ou mais recentes não interagidos
  const auxVerify = flatOpenItens?.filter((item: any) => {
    const value = item?.item?.[0]?.answer?.[0]?.valueString;
    const isCurrentEncounter = item?.displayInfo?.encounterId === state?.encounterReference?.id;
    const hasStatus = !!item?.item?.[1]?.answer?.[0]?.valueCoding;
    const hasNote = !!item?.item?.[2]?.answer?.[0]?.valueString;
    
    // Itens do encounter atual sempre aparecem
    if (isCurrentEncounter) {
      return true;
    }
    
    // Para itens de encounters anteriores:
    
    // 1. Encontre todos os itens com o mesmo valor/conduta
    const sameItems = flatOpenItens?.filter((el: any) => 
      el?.item?.[0]?.answer?.[0]?.valueString === value
    );
    
    // 2. Ordene por data (mais recentes primeiro)
    const sortedItems = [...sameItems].sort((a, b) => 
      new Date(b.awnserDate).getTime() - new Date(a.awnserDate).getTime()
    );
    
    // 3. Verifique se o item atual é o mais recente
    const isLatestItem = sortedItems[0]?.displayInfo?.encounterId === item?.displayInfo?.encounterId;
    
    // 4. Contagem total de itens para esta conduta
    const totalCount = sortedItems.length;
    const isPair = totalCount % 2 === 0;
    
    // 5. Verifique se já existe algum item com status para esta conduta
    const anyItemHasStatus = sameItems.some(
      (el: any) => !!el?.item?.[1]?.answer?.[0]?.valueCoding
    );
        
    // Regras de exibição:
    // - Se não for o item mais recente, não mostrar
    if (!isLatestItem) {
      return false;
    }
    
    // - Se for par E já tiver algum item com status, não mostrar
    if (isPair && anyItemHasStatus) {
      return false;
    }
    
    // - Se for ímpar, mostrar
    return !isPair || !anyItemHasStatus;
  });

  // Texto completo das condutas em aberto, para a checagem de duplicata em
  // AddConduta (o texto exibido é truncado em 100 caracteres).
  const openCondutas =
    auxVerify?.map((el: any) => el?.item?.[0]?.answer?.[0]?.valueString) || [];

  return (
    <>
      <Stack
        width="100%"
        maxHeight="60vh"
        overflow="auto"
        spacing={1}
        sx={{ padding: 2, ...scrollbarStyle }}
      >
        <AddConduta openItens={openCondutas} />
        <Divider orientation="horizontal" />
        <Stack direction="row" gap={1} alignItems="center">
          <Typography
            variant="body1"
            sx={{ my: 2, fontWeight: 500, fontSize: 16, color: "#555" }}
          >
            Lista de condutas em aberto
          </Typography>
          <Chip
            label={auxVerify?.length}
            size={"small"}
            sx={{ fontWeight: 500, fontSize: 12 }}
          />
        </Stack>
        <div className="lista-condutas-abertas">
        {auxVerify?.map((el: any, index: number) => {
          return (
            <Accordion
              defaultExpanded={index === 0}
              sx={{
                width: "100%",
                boxShadow: "1px 1px 16px 1px rgba(0, 0, 0, 0.1)",
                position: "initial",
                border: "1px solid",
                borderColor: "neutral200.main",
                borderRadius: 1,
              }}
            >
              <AccordionSummary
                expandIcon={<GridExpandMoreIcon />}
                aria-controls="panel1-content"
                id="panel1-header"
              >
                <Stack
                  direction="row"
                  width="100%"
                  justifyContent="space-between"
                >
                  {el?.item?.[0]?.answer?.[0]?.valueString.length > 100 ? (
                    <Tooltip
                      placement="right"
                      title={el?.item?.[0]?.answer?.[0]?.valueString}
                      slotProps={{
                        popper: {
                          modifiers: [
                            {
                              name: "offset",
                              options: {
                                offset: [0, -90],
                              },
                            },
                          ],
                        },
                      }}
                    >
                      <Typography
                        fontWeight={500}
                        sx={{
                          color: "#555",
                          wordBreak: "break-word",
                        }}
                      >
                        {el?.item?.[0]?.answer?.[0]?.valueString.substring(
                          0,
                          100
                        )}
                        ...
                      </Typography>
                    </Tooltip>
                  ) : (
                    <Typography
                      fontWeight={500}
                      sx={{
                        color: "#555",
                        wordBreak: "break-word",
                      }}
                    >
                      {el?.item?.[0]?.answer?.[0]?.valueString}
                    </Typography>
                  )}

                  <Stack direction="row" spacing={2} mr={1}>
                    {el?.displayInfo?.healthcare && (
                      <Chip
                        label={
                          el?.displayInfo?.healthcare || "Sem especialidade"
                        }
                        size="small"
                        sx={{
                          color: el?.displayInfo?.healthcare
                            ? "neutral700.main"
                            : "neutral500.main",

                          fontWeight: 500,
                          backgroundColor: el?.displayInfo?.healthcare
                            ? ""
                            : "#f1f1f1",
                          width: "fit-content",
                        }}
                      />
                    )}
                    <Chip
                      label={dayjs(el?.awnserDate)
                        ?.format("DD MMM YYYY")
                        ?.toUpperCase()}
                      size="small"
                      sx={{ fontWeight: 500, fontSize: 12, color: "#555" }}
                    />
                  </Stack>
                </Stack>
              </AccordionSummary>
              <AccordionDetails>
                <StatusAndNote
                  index={index}
                  type="CondutasPassadas"
                  answer={el?.item?.[0]?.answer?.[0]?.valueString}
                />

                {/* {el?.displayInfo?.identifier === user?.username ? (
                  <StatusAndNote
                    index={index}
                    type="CondutasPassadas"
                    answer={el?.item?.[0]?.answer?.[0]?.valueString}
                  />
                ) : (
                  <Stack
                    direction="row"
                    //bgcolor="primary200.main"
                    // bgcolor={bgColor ? `${bgColor}` : "transparent"}
                    alignItems="center"
                    spacing={1}
                    width="100%"
                    height="60px"
                    justifyContent="left"
                  >
                    <InfoOutlined />
                    <Typography variant="subtitle2" fontWeight={400}>
                      Conduta adicionada por outro profissional
                    </Typography>
                  </Stack>
                )} */}
              </AccordionDetails>

              <Stack
                sx={{ padding: 2 }}
                direction="row"
                width="100%"
                alignItems="center"
              >
                {el?.displayInfo?.practitioner && (
                  <Typography
                    color="neutral700.main"
                    sx={{ fontWeight: 500, fontStyle: "italic", fontSize: 12 }}
                  >
                    Dr. {el?.displayInfo?.practitioner}
                  </Typography>
                )}
              </Stack>
            </Accordion>
          );
        })}
        </div>
      </Stack>

      {!openDialog ? (
        <Stack
          width="100%"
          sx={{ ...scrollbarStyle }}
          overflow="auto"
          height="60vh"
          paddingRight={3}
        >
          {!luxorMode && (
            <CondutasHistory filter={""} />
          )}
          {luxorMode && (
            <LuxorMed />
          )}
        </Stack>
      ) : (
        <></>
      )}
    </>
  );
}
